import React, { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Check, CheckCircle2, Clock, Crown, Eye, Plus, User, UserCheck, Users } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useUsers } from '../hooks/useUsers';
import { useTeams, formatTeamName, type Team } from '../hooks/useTeams';
import {
  useCMOwners,
  useCMApprovers,
  useCMWatchers,
  useCMAssignees,
  useAddOwner,
  useAddApprover,
  useAddWatcher,
  useAddAssignee,
  type RoleResponse,
  type AddRoleRequest,
} from '../hooks/useChangeManagements';
import { AppSheet } from '@/components/ui/app-sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

type RoleType = 'owner' | 'approver' | 'watcher' | 'assignee';

const ROLE_LABEL: Record<RoleType, string> = {
  owner: 'Owner',
  approver: 'Approver',
  watcher: 'Watcher',
  assignee: 'Assignee',
};

interface ParticipantsPanelProps {
  cmId: string;
}

interface AddParticipantModalProps {
  isActive: boolean;
  roleType: RoleType;
  onClose: () => void;
  onAdd: (data: AddRoleRequest) => void;
}

const AddParticipantModal: React.FC<AddParticipantModalProps> = ({
  isActive,
  roleType,
  onClose,
  onAdd,
}) => {
  const { user } = useAuth();
  const organizationId = user?.organizationId;

  const [selectedType, setSelectedType] = useState<'user' | 'team'>('user');
  const [selectedId, setSelectedId] = useState('');

  const { data: users } = useUsers(organizationId);
  const { data: teams } = useTeams(organizationId);

  const handleSubmit = (e: React.SubmitEvent) => {
    e.preventDefault();
    if (!selectedId) return;
    onAdd({ roleType: selectedType, roleId: selectedId });
    setSelectedId('');
    setSelectedType('user');
    onClose();
  };

  return (
    <AppSheet
      open={isActive}
      onOpenChange={(open) => { if (!open) onClose(); }}
      title={`Add ${ROLE_LABEL[roleType]}`}
      size="sm"
    >
      <AppSheet.Body>
        <form id="add-participant-form" onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="ap-type">Type</Label>
            <Select
              value={selectedType}
              onValueChange={(v) => {
                setSelectedType(v as 'user' | 'team');
                setSelectedId('');
              }}
            >
              <SelectTrigger id="ap-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="user">User</SelectItem>
                <SelectItem value="team">Team</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ap-id">{selectedType === 'user' ? 'User' : 'Team'}</Label>
            <Select value={selectedId} onValueChange={setSelectedId} required>
              <SelectTrigger id="ap-id">
                <SelectValue placeholder={`Select ${selectedType === 'user' ? 'a user' : 'a team'}`} />
              </SelectTrigger>
              <SelectContent>
                {selectedType === 'user'
                  ? users?.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.firstName} {u.lastName} ({u.email})
                      </SelectItem>
                    ))
                  : teams?.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {formatTeamName(t)}
                      </SelectItem>
                    ))}
              </SelectContent>
            </Select>
          </div>
        </form>
      </AppSheet.Body>
      <AppSheet.Footer>
        <Button variant="outline" type="button" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" form="add-participant-form" disabled={!selectedId}>
          Add
        </Button>
      </AppSheet.Footer>
    </AppSheet>
  );
};

interface ParticipantItemProps {
  participant: RoleResponse;
  users?: { id: string; firstName: string; lastName: string; email: string }[];
  teams?: Team[];
  showApprovalStatus?: boolean;
}

const ParticipantItem: React.FC<ParticipantItemProps> = ({
  participant,
  users,
  teams,
  showApprovalStatus = false,
}) => {
  const getName = () => {
    if (participant.roleType === 'user') {
      const u = users?.find((x) => x.id === participant.roleId);
      return u ? `${u.firstName} ${u.lastName}` : participant.roleId;
    }
    const team = teams?.find((t) => t.id === participant.roleId);
    return team ? formatTeamName(team) : participant.roleId;
  };

  const Icon = participant.roleType === 'user' ? User : Users;

  return (
    <div className="rounded-md border border-border-subtle bg-card p-3 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Icon className="w-4 h-4 text-muted-foreground" />
          <span className="truncate">{getName()}</span>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Added {formatDistanceToNow(new Date(participant.addedAt), { addSuffix: true })}
        </p>
      </div>
      {showApprovalStatus ? (
        participant.approvalTime ? (
          <Badge className="bg-sev-4/15 text-sev-4 border-0 gap-1 font-normal">
            <Check className="w-3 h-3" />
            Approved
          </Badge>
        ) : (
          <Badge className="bg-sev-3/15 text-foreground border-0 gap-1 font-normal">
            <Clock className="w-3 h-3" />
            Pending
          </Badge>
        )
      ) : null}
    </div>
  );
};

interface SectionProps {
  title: string;
  icon: React.ReactNode;
  onAdd: () => void;
  emptyText: string;
  participants?: RoleResponse[];
  users?: { id: string; firstName: string; lastName: string; email: string }[];
  teams?: Team[];
  showApprovalStatus?: boolean;
}

function Section({
  title,
  icon,
  onAdd,
  emptyText,
  participants,
  users,
  teams,
  showApprovalStatus,
}: SectionProps) {
  const isEmpty = !participants || participants.length === 0;
  return (
    <div className="rounded-lg border border-border bg-card p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          {icon}
          {title}
        </h3>
        <Button variant="outline" size="sm" onClick={onAdd} className="gap-1.5">
          <Plus className="w-3.5 h-3.5" />
          Add
        </Button>
      </div>
      {isEmpty ? (
        <p className="text-xs text-muted-foreground">{emptyText}</p>
      ) : (
        <div className="space-y-2">
          {participants.map((p) => (
            <ParticipantItem
              key={`${p.roleType}-${p.roleId}`}
              participant={p}
              users={users}
              teams={teams}
              showApprovalStatus={showApprovalStatus}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function ParticipantsPanel({ cmId }: ParticipantsPanelProps) {
  const { user } = useAuth();
  const organizationId = user?.organizationId;

  const [activeModal, setActiveModal] = useState<RoleType | null>(null);

  const { data: owners, isLoading: ownersLoading } = useCMOwners(cmId);
  const { data: approvers, isLoading: approversLoading } = useCMApprovers(cmId);
  const { data: watchers, isLoading: watchersLoading } = useCMWatchers(cmId);
  const { data: assignees, isLoading: assigneesLoading } = useCMAssignees(cmId);

  const { data: users } = useUsers(organizationId);
  const { data: teams } = useTeams(organizationId);

  const addOwnerMutation = useAddOwner();
  const addApproverMutation = useAddApprover();
  const addWatcherMutation = useAddWatcher();
  const addAssigneeMutation = useAddAssignee();

  const handleAddParticipant = (type: RoleType, data: AddRoleRequest) => {
    const mutations = {
      owner: addOwnerMutation,
      approver: addApproverMutation,
      watcher: addWatcherMutation,
      assignee: addAssigneeMutation,
    };
    mutations[type].mutate({ cmId, data });
  };

  if (ownersLoading || approversLoading || watchersLoading || assigneesLoading) {
    return (
      <div className="rounded-lg border border-border bg-card p-4">
        <Progress value={undefined} className="h-1" />
      </div>
    );
  }

  return (
    <div>
      <Section
        title="Owners"
        icon={<Crown className="w-4 h-4 text-muted-foreground" />}
        onAdd={() => { setActiveModal('owner'); }}
        emptyText="No owners assigned"
        participants={owners}
        users={users}
        teams={teams}
      />
      <Section
        title="Approvers"
        icon={<CheckCircle2 className="w-4 h-4 text-muted-foreground" />}
        onAdd={() => { setActiveModal('approver'); }}
        emptyText="No approvers assigned"
        participants={approvers}
        users={users}
        teams={teams}
        showApprovalStatus
      />
      <Section
        title="Watchers"
        icon={<Eye className="w-4 h-4 text-muted-foreground" />}
        onAdd={() => { setActiveModal('watcher'); }}
        emptyText="No watchers assigned"
        participants={watchers}
        users={users}
        teams={teams}
      />
      <Section
        title="Assignees"
        icon={<UserCheck className="w-4 h-4 text-muted-foreground" />}
        onAdd={() => { setActiveModal('assignee'); }}
        emptyText="No assignees"
        participants={assignees}
        users={users}
        teams={teams}
      />
      {activeModal ? (
        <AddParticipantModal
          isActive
          roleType={activeModal}
          onClose={() => { setActiveModal(null); }}
          onAdd={(data) => { handleAddParticipant(activeModal, data); }}
        />
      ) : null}
    </div>
  );
}
